import React, { useState } from 'react';
import {
  Typography, Form, Input, InputNumber, Select, Button, Upload, message,
  Card, Row, Col, Steps, Divider, Checkbox, Space,
} from 'antd';
import {
  InfoCircleOutlined,
  ShopOutlined, CalendarOutlined,
  DollarOutlined, TagsOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  useCreateDealMutation,
  useListMerchantsQuery,
  useListCategoriesQuery,
  uploadImage,
} from './dealsApi';

const { TextArea } = Input;
const { Title, Text } = Typography;

const steps = [
  { title: 'Product Info', icon: <TagsOutlined /> },
  { title: 'Pricing', icon: <DollarOutlined /> },
  { title: 'Store & Link', icon: <ShopOutlined /> },
  { title: 'Review', icon: <InfoCircleOutlined /> },
];

const STEP_FIELDS = [
  ['title'],
  ['price'],
  ['merchantId', 'affiliateLink'],
  [],
];

function indentCategoryName(name, depth) {
  if (!depth) return name;
  return `${'— '.repeat(depth)}${name}`;
}

export default function DealSubmitPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [createDeal, { isLoading: submitting }] = useCreateDealMutation();
  const { data: merchantsData } = useListMerchantsQuery();
  const { data: categoriesData } = useListCategoriesQuery();
  const [imageUrl, setImageUrl] = useState(null);

  const merchants = merchantsData?.items || [];
  const categories = categoriesData?.items || [];

  const handleImageRemove = () => {
    setImageUrl(null);
  };

  const handleSubmit = async () => {
    try {
      // All steps stay mounted (display:none) so every field is registered.
      const values = await form.validateFields();
      const payload = {
        title: values.title,
        description: values.description,
        dealUrl: values.affiliateLink,
        priceMinor: Math.round(Number(values.price) * 100),
        retailPriceMinor: values.retailPrice != null
          ? Math.round(Number(values.retailPrice) * 100)
          : null,
        currency: values.currency || 'USD',
        merchantId: values.merchantId ?? null,
        categoryId: values.categoryId ?? null,
        condition: values.condition || 'new',
        isFreeShipping: values.isFreeShipping || false,
        imageUrls: imageUrl ? [imageUrl] : undefined,
      };
      const result = await createDeal(payload).unwrap();
      message.success('Deal submitted for review!');
      navigate(`/deals/${result.publicId}`);
    } catch (err) {
      if (err?.errorFields) {
        // Jump to the first step that has a validation error
        const bad = err.errorFields[0]?.name?.[0];
        if (bad && STEP_FIELDS[0].includes(bad)) setCurrentStep(0);
        else if (bad && STEP_FIELDS[1].includes(bad)) setCurrentStep(1);
        else if (bad && STEP_FIELDS[2].includes(bad)) setCurrentStep(2);
      } else {
        message.error(err?.data?.title || 'Failed to submit deal');
      }
    }
  };

  const next = async () => {
    try {
      await form.validateFields(STEP_FIELDS[currentStep]);
      setCurrentStep(currentStep + 1);
    } catch (_) {}
  };

  const prev = () => setCurrentStep(currentStep - 1);

  const previewPrice = form.getFieldValue('price');

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <div style={{ marginBottom: 32 }}>
        <Title level={3} style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>
          Submit a Deal
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Share a great deal with the DealFusion community. All submissions are reviewed before publishing.
        </Text>
      </div>

      <Steps
        current={currentStep}
        items={steps}
        style={{ marginBottom: 32 }}
        size="small"
      />

      <Card style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <Form
          form={form}
          layout="vertical"
          scrollToFirstError
        >
          {/* Keep all steps mounted so Form retains + validates values across wizard steps */}
          <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
              <Form.Item
                label="Deal Title"
                name="title"
                rules={[{ required: true, min: 5, max: 500, message: 'Title must be at least 5 characters' }]}
              >
                <Input
                  placeholder="e.g. Sony WH-1000XM5 Wireless Headphones — 35% off"
                  size="large"
                  maxLength={120}
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>

              <Form.Item
                label="Description (Optional)"
                name="description"
              >
                <TextArea
                  rows={4}
                  placeholder="Provide more details about the deal, key features, or terms…"
                  maxLength={500}
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>

              <Form.Item
                label="Deal Image"
                name="imageFile"
                extra="PNG, JPG — max 5MB. The first image becomes the cover."
              >
                <Upload
                  listType="picture-card"
                  fileList={imageUrl ? [{ uid: 'uploaded', name: 'deal-image', status: 'done', url: imageUrl }] : []}
                  beforeUpload={(file) => {
                    const isImg = file.type.startsWith('image/');
                    const isLt5M = file.size / 1024 / 1024 < 5;
                    if (!isImg) {
                      message.error('Only image files are allowed');
                      return false;
                    }
                    if (!isLt5M) {
                      message.error('Image must be smaller than 5MB');
                      return false;
                    }
                    uploadImage(file)
                      .then((res) => {
                        const url = res?.url;
                        if (!url) {
                          message.error('Upload succeeded but no image URL was returned');
                          return;
                        }
                        setImageUrl(url);
                        message.success('Image uploaded');
                      })
                      .catch((err) => {
                        message.error(err?.message || 'Upload failed. Check ImageKit configuration.');
                      });
                    return false;
                  }}
                  onRemove={handleImageRemove}
                  onPreview={() => {}}
                  accept="image/*"
                  maxCount={1}
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
          </div>

          <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Price"
                    name="price"
                    rules={[{ required: true, type: 'number', min: 0.01, message: 'Enter a valid price' }]}
                  >
                    <InputNumber
                      prefix={<DollarOutlined style={{ color: '#94a3b8' }} />}
                      placeholder="0.00"
                      size="large"
                      style={{ width: '100%', borderRadius: 8 }}
                      min={0.01}
                      step={0.01}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Retail Price (Optional)"
                    name="retailPrice"
                  >
                    <InputNumber
                      prefix={<DollarOutlined style={{ color: '#94a3b8' }} />}
                      placeholder="0.00"
                      size="large"
                      style={{ width: '100%', borderRadius: 8 }}
                      min={0.01}
                      step={0.01}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Currency"
                name="currency"
                initialValue="USD"
              >
                <Select style={{ width: '100%', borderRadius: 8 }} size="large">
                  <Select.Option value="USD">USD — US Dollar</Select.Option>
                  <Select.Option value="EUR">EUR — Euro</Select.Option>
                  <Select.Option value="GBP">GBP — British Pound</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Expires At (Optional)"
                name="expiresAt"
              >
                <Input
                  type="date"
                  size="large"
                  style={{ borderRadius: 8 }}
                  prefix={<CalendarOutlined style={{ color: '#94a3b8' }} />}
                />
              </Form.Item>

              <Form.Item
                label="Condition"
                name="condition"
                initialValue="new"
              >
                <Select style={{ width: '100%', borderRadius: 8 }} size="large">
                  <Select.Option value="new">Brand New</Select.Option>
                  <Select.Option value="refurbished">Refurbished</Select.Option>
                  <Select.Option value="used_open_box">Open Box</Select.Option>
                  <Select.Option value="used">Used</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Category"
                name="categoryId"
              >
                <Select
                  style={{ width: '100%', borderRadius: 8 }}
                  size="large"
                  placeholder="Select a category"
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  options={categories.map((c) => ({
                    value: c.id,
                    label: indentCategoryName(c.name, c.depth),
                  }))}
                />
              </Form.Item>

              <Form.Item
                name="isFreeShipping"
                valuePropName="checked"
              >
                <Checkbox style={{ fontSize: 14 }}>Free shipping</Checkbox>
              </Form.Item>
          </div>

          <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>
              <Form.Item
                label="Merchant / Retailer"
                name="merchantId"
                rules={[{ required: true, message: 'Select a merchant' }]}
              >
                <Select
                  placeholder="Search or select a merchant"
                  showSearch
                  size="large"
                  style={{ width: '100%', borderRadius: 8 }}
                  optionFilterProp="label"
                  options={merchants.map((m) => ({
                    value: m.id,
                    label: m.name,
                  }))}
                />
              </Form.Item>

              <Form.Item
                label="Affiliate Link"
                name="affiliateLink"
                rules={[
                  { required: true, message: 'Affiliate link is required' },
                  { type: 'url', message: 'Enter a valid URL' },
                  {
                    validator: (_, value) => {
                      if (!value || String(value).startsWith('https://')) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('URL must use HTTPS'));
                    },
                  },
                ]}
              >
                <Input
                  placeholder="https://..."
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>

              <Form.Item
                label="Tags"
                name="tags"
                extra="Helps users find your deal"
              >
                <Select
                  mode="tags"
                  placeholder="e.g. electronics, headphones, audio"
                  size="large"
                  style={{ width: '100%', borderRadius: 8 }}
                  options={[
                    { value: 'electronics', label: 'Electronics' },
                    { value: 'audio', label: 'Audio' },
                    { value: 'computers', label: 'Computers' },
                  ]}
                />
              </Form.Item>
          </div>

          {currentStep === 3 && (
            <div style={{ padding: '12px 0', textAlign: 'center' }}>
              <img
                src={imageUrl || 'https://via.placeholder.com/300x200?text=No+Image'}
                alt="Preview"
                style={{ maxWidth: '100%', borderRadius: 12, marginBottom: 24, maxHeight: 200, objectFit: 'cover' }}
              />
              <div style={{ textAlign: 'left' }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {form.getFieldValue('title') || 'Untitled deal'}
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  {previewPrice != null && previewPrice !== ''
                    ? `$${Number(previewPrice).toFixed(2)}`
                    : 'Price not set'}
                </Text>
                {form.getFieldValue('tags') && (
                  <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Tags: {form.getFieldValue('tags').join(', ')}
                  </Text>
                )}
                <Divider style={{ margin: '16px 0' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Review your deal</Text>
                </Divider>
              </div>
            </div>
          )}
        </Form>
      </Card>

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
        <Button onClick={currentStep === 0 ? () => navigate('/') : prev} size="large" style={{ borderRadius: 8 }}>
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </Button>
        <Space>
          {currentStep < steps.length - 1 ? (
            <Button type="primary" size="large" onClick={next} style={{ borderRadius: 8, fontWeight: 600 }}>
              Continue
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              loading={submitting}
              onClick={handleSubmit}
              style={{ borderRadius: 8, fontWeight: 600, padding: '8px 32px' }}
            >
              {submitting ? 'Submitting...' : 'Submit Deal'}
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
}
